import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { timer, throwError } from 'rxjs';
import { mapTo, tap, map } from 'rxjs/operators';

import { HttpClient, HttpHeaders } from '@angular/common/http';

import { AuthStore, ReturnAuthState } from './auth.store';

import { environment } from '@env/environment';

export interface Backdoor {
  uid: string;
  token: string;
  tstamp: string;
}

export interface Credentials {
  email: string;
  password: string;
}
const HttpUploadOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  }),
};

const HttpUploadOptionsWithAuth = (token?: string) => {
  const headers: any = {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return { headers: new HttpHeaders(headers) };
};

@Injectable({
  providedIn: 'root',
})
export class AuthHttpService {
  constructor(private http: HttpClient) {}

  public login(creds: Credentials): Observable<ReturnAuthState> {
    let input = new FormData();
    // Add your values in here
    input.append('email', creds.email);
    input.append('password', creds.password);

    // rest api call - using monitoring auth endpoint
    return this.http.post<any>(`${environment.monitoringAuthUrl}/login`, JSON.stringify(creds), HttpUploadOptions).pipe(
      map((data) => {
        //console.log(data);
        return data;
      })
    );
  }

  public firststart(inputdata: Backdoor): Observable<ReturnAuthState> {
    let input = new FormData();
    // Add your values in here
    //input.append('uid', inputdata.uid);
    input.append('token', inputdata.token);
    //input.append('tstamp', inputdata.tstamp);

    // rest api call - using protected API endpoint
    return this.http.post<any>(`${environment.apiUrl}/firststart`, JSON.stringify(inputdata), HttpUploadOptions).pipe(
      map((data) => {
        //console.log(data);
        return data;
      })
    );
  }

  logout() {
    return this.http.get<any>(`${environment.monitoringAuthUrl}/logout`, HttpUploadOptions).pipe(
      map((data) => {
        return data;
      })
    );
  }

  register(data: any) {
    // rest api call - using new auth endpoint
    return this.http.post<any>(`${environment.authUrl}/register`, JSON.stringify(data), HttpUploadOptions).pipe(
      map((data) => {
        //console.log(data);
        return data;
      })
    );
  }

  changePassword(data: any) {
    // rest api call - using monitoring auth endpoint
    return this.http.post<any>(`${environment.monitoringAuthUrl}/change-pwd`, JSON.stringify(data), HttpUploadOptions).pipe(
      map((data) => {
        //console.log(data);
        return data;
      })
    );
  }

  forgotPassword(data: any) {
    // rest api call - using new auth endpoint
    return this.http.post<any>(`${environment.authUrl}/forgot-pwd`, JSON.stringify(data), HttpUploadOptions).pipe(
      map((data) => {
        //console.log(data);
        return data;
      })
    );
  }

  loginWithToken(token: string, accountId: number) {
    // rest api call - using monitoring backdoor login endpoint (belogin) which accepts token and uid
    const loginData = {
      uid: accountId.toString(),
      token: token
    };
    return this.http.post<any>(`${environment.monitoringAuthUrl}/belogin`, JSON.stringify(loginData), HttpUploadOptions).pipe(
      map((data) => {
        //console.log(data);
        return data;
      })
    );
  }

  activateAccount(token: string, accountId: number) {
    // rest api call - using account activation endpoint
    return this.http.get<any>(`${environment.monitoringAuthUrl}/activate?token=${encodeURIComponent(token)}&account_id=${accountId}`, HttpUploadOptions).pipe(
      map((data) => {
        return data;
      })
    );
  }

  sendEmailVerification(token?: string, activationToken?: string, accountId?: number) {
    // rest api call - can use JWT token (sent in header) OR activation token + account_id (sent in body)
    const options = token ? HttpUploadOptionsWithAuth(token) : HttpUploadOptions;
    
    // Prepare request body - include activation token and account_id if JWT token is not available
    const requestBody: any = {};
    if (!token && activationToken && accountId) {
      requestBody.activation_token = activationToken;
      requestBody.account_id = accountId;
    }
    
    return this.http.post<any>(`${environment.apiUrl}/auth/send-email-verification`, requestBody, options).pipe(
      map((data) => {
        return data;
      })
    );
  }

  verifyEmailCode(code: string, token?: string, activationToken?: string, accountId?: number) {
    // rest api call - can use JWT token (sent in header) OR activation token + account_id (sent in body)
    const options = token ? HttpUploadOptionsWithAuth(token) : HttpUploadOptions;
    
    // Prepare request body - include activation token and account_id if JWT token is not available
    const requestBody: any = { code };
    if (!token && activationToken && accountId) {
      requestBody.activation_token = activationToken;
      requestBody.account_id = accountId;
    }
    
    return this.http.post<any>(`${environment.apiUrl}/auth/verify-email-code`, JSON.stringify(requestBody), options).pipe(
      map((data) => {
        return data;
      })
    );
  }

  requestNewActivationToken(email: string) {
    // rest api call - public endpoint
    return this.http.post<any>(`${environment.apiUrl}/auth/request-new-activation-token`, JSON.stringify({ email }), HttpUploadOptions).pipe(
      map((data) => {
        return data;
      })
    );
  }

  private handleError(error: Response | any) {
    console.error('ApiService::handleError', error);
    return throwError(() => error);
  }
}
